import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { resolveCongressCountdownCopy } from "../../domains/congress/congressCountdownCopy";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { useNow } from "../../common/hooks/useNow";
import { resolveCongressNames } from "../../domains/congress/congressName";
import {
  deriveCongressCountdown,
  isCongressHeroVisible,
} from "../../domains/congress/deriveCongressCountdown";
import { deriveCongressPhase } from "../../../../api/src/shared/domain/congress-lifecycle";
import { buildCongressDateLabel } from "../../domains/congress/congressDateLabel";
import {
  gaEventCongressCountdownShown,
  gaEventCongressCountdownTapped,
  type CongressAnalyticsContext,
} from "../../tools/analytics/domains/congress.analytics";
import { useApiQuery } from "../../services/api";
import { CongressCard } from "../ui/CongressCard";
import { DEFAULT_COVER_COLOR } from "../../domains/congress/coverColor";
import { deriveCongressStatus } from "../../domains/congress/congressStatus";
import { isDesktopLayout } from "../../common/hooks/useIsDesktopView";
import { DesktopCongressBanner } from "../congress/DesktopCongressBanner";
import { cn } from "../../lib/utils";

const MINUTE_MS = 60_000;

export function RoomCongressHero({
  roomId,
  organisationName,
  accentColor,
  frame,
}: {
  roomId: string;
  /**
   * `padded`: inside a page that gives its blocks no inset (the phone).
   * `flush`: in the desktop room shell, whose column
   * already places it (Figma "D3.explore.room.congress-banner").
   */
  frame: "padded" | "flush";
  /** The room's partner: the desktop banner signs the space with it */
  organisationName: string;
  accentColor: string;
}) {
  const history = useHistory();
  // ONE binding on purpose. Every string this hero draws — name, place, date,
  // countdown units, the live pill — is congress content, so it follows
  // `contentLang`. `activeLang` has no job here; the chrome around the hero is
  // rendered by its parents. See the axis rule in `useActiveLang`.
  const { contentLang } = useActiveLang();
  const { t } = useTranslation();
  const countdownCopy = resolveCongressCountdownCopy({ contentLang, t });
  const now = useNow(MINUTE_MS);
  const result = useApiQuery("/webapp/private/rooms/:id/congress-spaces", {
    param: { id: roomId },
    query: { language: contentLang },
  });

  const cards =
    result.state !== "success"
      ? []
      : result.body
          .map((space) => {
            // The hero is opt-in per space; non-promoted spaces live in Resources only.
            if (!space.promoteOnDiscovery) return null;
            const countdown = deriveCongressCountdown(space, now);
            // In the hero while the countdown runs, while the congress is live,
            // and through its post-congress grace window — the same window that
            // keeps the room's countdown card up, so the two cannot disagree.
            if (!isCongressHeroVisible(space, now)) return null;
            const names = resolveCongressNames(
              space.translations,
              space.fallbackName,
              contentLang,
              space.city,
              space.country
            );
            return {
              id: space.id,
              name: names.name,
              startDate: space.startDate,
              endDate: space.endDate,
              city: names.city,
              country: names.country,
              // Name, place AND date all follow `contentLang` — the card reads as
              // one language or it reads as none. See `useActiveLang`.
              dateLabel: buildCongressDateLabel(
                {
                  startDate: space.startDate,
                  endDate: space.endDate,
                  city: names.city,
                  country: names.country,
                },
                {
                  contentLang,
                  monthStyle: "long",
                  placeSeparator: " | ",
                }
              ),
              coverColor:
                space.coverColor === null
                  ? DEFAULT_COVER_COLOR
                  : space.coverColor,
              coverTextMode: space.coverTextMode,
              cover: space.cover,
              countdown,
              status: deriveCongressStatus(space, now),
              analytics: {
                spaceId: space.id,
                congressName: names.name,
                phase: deriveCongressPhase(space, now),
                roomId,
              } satisfies CongressAnalyticsContext,
            };
          })
          .filter((card) => card !== null);

  useEffectOnce(() => {
    for (const card of cards) {
      gaEventCongressCountdownShown({
        context: card.analytics,
        surface: "room_hero",
      });
    }
  }, [cards.map((card) => card.id).join(",")]);

  if (cards.length === 0) return null;

  // Desktop: the space is a page of its own, under the room, and the banner
  // opens it. Mobile keeps the Resources section the card has always opened.
  if (isDesktopLayout()) {
    return (
      <div
        className={cn(
          "flex flex-col gap-3",
          frame === "padded" ? "px-5 pb-2 pt-4" : null
        )}
      >
        {cards.map((card) => (
          <DesktopCongressBanner
            key={card.id}
            name={card.name}
            dateLabel={buildCongressDateLabel(
              {
                startDate: card.startDate,
                endDate: card.endDate,
                city: card.city,
                country: card.country,
              },
              { contentLang, monthStyle: "short", placeSeparator: " · " }
            )}
            cover={card.cover}
            coverColor={card.coverColor}
            status={card.status}
            organisationName={organisationName}
            accentColor={accentColor}
            contentLang={contentLang}
            countdownCopy={countdownCopy}
            onOpen={() => {
              gaEventCongressCountdownTapped({
                context: card.analytics,
                surface: "room_hero",
              });
              history.push({
                pathname: `/room/congress/${roomId}/${card.id}`,
                state: { congressEntryPoint: "room_block" },
              });
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 px-4 pb-2 pt-4">
      {cards.map((card) => (
        <CongressCard
          key={card.id}
          name={card.name}
          dateLabel={card.dateLabel}
          coverColor={card.coverColor}
          coverTextMode={card.coverTextMode}
          display={{ variant: "full", cover: card.cover }}
          countdown={card.countdown}
          countdownCopy={countdownCopy}
          onClick={() => {
            gaEventCongressCountdownTapped({
              context: card.analytics,
              surface: "room_hero",
            });
            // Open the room's Resources tab filtered to this congress.
            history.push({
              pathname: `/room/resources/${roomId}`,
              search: `?section=congress:${card.id}`,
              state: { congressEntryPoint: "room_block" },
            });
          }}
        />
      ))}
    </div>
  );
}
